const EAF_FILE_MAGIC = 0x89
const EAF_MAGIC_HEAD = 0x5a5a

function checksum8(bytes) {
  let sum = 0
  for (let i = 0; i < bytes.length; i++) sum = (sum + bytes[i]) >>> 0
  return sum >>> 0
}

function decodeRle(encoded, expectedSize) {
  const out = new Uint8Array(expectedSize)
  let inPos = 0
  let outPos = 0

  while (inPos + 1 < encoded.length && outPos < expectedSize) {
    const repeatCount = encoded[inPos++]
    const repeatValue = encoded[inPos++]
    const end = Math.min(expectedSize, outPos + repeatCount)
    out.fill(repeatValue, outPos, end)
    outPos = end
  }

  return out
}

function parseFrameHeader(frameBytes) {
  const dv = new DataView(frameBytes.buffer, frameBytes.byteOffset, frameBytes.byteLength)
  const format = String.fromCharCode(frameBytes[0] || 0, frameBytes[1] || 0)
  // EmoteDisplay EAFs sometimes append a trailing `_C` frame with conversion metadata.
  // That frame isn't an image; ignore it so the animation still plays.
  if (format === '_C') return { format, kind: 'comment' }
  if (format !== '_S') throw new Error(`Unsupported EAF frame format: ${format || '??'}`)

  const bitDepth = frameBytes[9]
  if (bitDepth !== 8) {
    throw new Error(`Unsupported EAF bit depth: ${bitDepth} (only 8-bit supported)`)
  }

  const width = dv.getUint16(10, true)
  const height = dv.getUint16(12, true)
  const blocks = dv.getUint16(14, true)
  const blockHeight = dv.getUint16(16, true)

  if (!width || !height || !blocks || !blockHeight) {
    throw new Error('Invalid EAF frame header')
  }

  const blockLens = []
  for (let i = 0; i < blocks; i++) blockLens.push(dv.getUint32(18 + i * 4, true))

  const numColors = 1 << bitDepth
  const paletteOffset = 18 + blocks * 4
  const paletteBytes = frameBytes.subarray(paletteOffset, paletteOffset + numColors * 4)
  const dataOffset = paletteOffset + numColors * 4

  if (paletteBytes.length !== numColors * 4) {
    throw new Error('Invalid EAF palette')
  }

  return { width, height, blocks, blockHeight, blockLens, paletteBytes, dataOffset }
}

function paletteBgraToRgba(paletteBgra) {
  const rgba = new Uint8ClampedArray(paletteBgra.length)
  for (let i = 0; i < paletteBgra.length; i += 4) {
    const b = paletteBgra[i]
    const g = paletteBgra[i + 1]
    const r = paletteBgra[i + 2]
    const a = paletteBgra[i + 3]

    rgba[i] = r
    rgba[i + 1] = g
    rgba[i + 2] = b
    rgba[i + 3] = r === 0 && g === 0 && b === 0 && a === 0 ? 0 : a || 255
  }
  return rgba
}

function decodeFrameToImageData(frameBytes) {
  const header = parseFrameHeader(frameBytes)
  if (header?.kind === 'comment') return null
  const { width, height, blocks, blockHeight, blockLens, paletteBytes, dataOffset } = header
  const paletteRgba = paletteBgraToRgba(paletteBytes)

  const indices = new Uint8Array(width * height)
  let cursor = dataOffset
  const blockExpected = width * blockHeight

  for (let block = 0; block < blocks; block++) {
    const len = blockLens[block] || 0
    const blockData = frameBytes.subarray(cursor, cursor + len)
    cursor += len

    const encoding = blockData[0]
    if (encoding !== 0) {
      throw new Error(`Unsupported EAF encoding: ${encoding} (only RLE=0 supported)`)
    }

    const decoded = decodeRle(blockData.subarray(1), blockExpected)
    const y0 = block * blockHeight
    const validRows = Math.max(0, Math.min(blockHeight, height - y0))
    if (!validRows) continue
    indices.set(decoded.subarray(0, validRows * width), y0 * width)
  }

  const rgba = new Uint8ClampedArray(indices.length * 4)
  for (let i = 0; i < indices.length; i++) {
    const pi = indices[i] * 4
    const oi = i * 4
    rgba[oi] = paletteRgba[pi]
    rgba[oi + 1] = paletteRgba[pi + 1]
    rgba[oi + 2] = paletteRgba[pi + 2]
    rgba[oi + 3] = paletteRgba[pi + 3]
  }

  return { width, height, imageData: new ImageData(rgba, width, height) }
}

function parseEafFile(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer)
  if (bytes[0] !== EAF_FILE_MAGIC) throw new Error('Invalid EAF/AAF file (bad magic)')

  const format = String.fromCharCode(bytes[1] || 0, bytes[2] || 0, bytes[3] || 0)
  if (format !== 'EAF' && format !== 'AAF') throw new Error(`Invalid EAF/AAF file (bad format: ${format})`)

  const dv = new DataView(arrayBuffer)
  const totalFrames = dv.getUint32(4, true)
  const storedChecksum = dv.getUint32(8, true)
  const storedLen = dv.getUint32(12, true)

  const tableOffset = 16
  const checksumBytes = bytes.subarray(tableOffset, tableOffset + storedLen)
  const calculated = checksum8(checksumBytes)
  if (storedLen && storedChecksum !== calculated) {
    throw new Error('Invalid EAF file (checksum mismatch)')
  }

  const frameDataOffset = tableOffset + totalFrames * 8
  const frames = []

  for (let i = 0; i < totalFrames; i++) {
    const entryOffset = tableOffset + i * 8
    const frameSize = dv.getUint32(entryOffset, true)
    const frameOffset = dv.getUint32(entryOffset + 4, true)
    const start = frameDataOffset + frameOffset
    const end = start + frameSize

    if (end > bytes.length) throw new Error('Invalid EAF file (frame out of range)')
    const magic = dv.getUint16(start, true)
    if (magic !== EAF_MAGIC_HEAD) throw new Error('Invalid EAF file (bad frame magic)')

    const frameBytes = bytes.subarray(start + 2, end)
    const decoded = decodeFrameToImageData(frameBytes)
    if (!decoded) continue
    frames.push(decoded.imageData)
  }

  const first = frames[0]
  if (!first) throw new Error('Invalid EAF file (no drawable frames)')
  return { width: first.width, height: first.height, frames }
}

const EAF_CACHE = new Map()

export function loadEaf(url) {
  const key = String(url || '')
  if (!key) return Promise.reject(new Error('Missing EAF url'))

  if (EAF_CACHE.has(key)) return EAF_CACHE.get(key)

  const promise = fetch(key)
    .then((r) => {
      if (!r.ok) throw new Error(`Failed to load EAF (${r.status})`)
      return r.arrayBuffer()
    })
    .then(parseEafFile)

  EAF_CACHE.set(key, promise)
  return promise
}
